import { z } from 'zod';

// Öffentlicher Eintragungs-Link: /join/<slug>
//
// Wer sich hier einträgt, hat KEIN Konto und ist nicht angemeldet. Der Endpunkt ist
// daher bewusst schmal — er legt genau einen Kontakt im Netzwerk des Gastgebers an.

/** Wer lädt ein — wird auf der Eintragungsseite gezeigt, damit klar ist, in wessen Netzwerk man landet. */
export const JoinHostDTO = z.object({
  name: z.string(),
  company: z.string().nullable(),
  role: z.string().nullable(),
  photoUrl: z.string().nullable(),
  pitch: z.string().nullable(),
});
export type JoinHostDTO = z.infer<typeof JoinHostDTO>;

/**
 * Wer schaut gerade auf den Link — nur gesetzt, wenn die Person angemeldet ist.
 * Damit kann die Seite den 6-Schritte-Wizard überspringen: Wer schon ein Nexply-Konto
 * hat, soll seine Daten nicht ein zweites Mal eintippen.
 */
export const JoinViewerDTO = z.object({
  name: z.string(),
  email: z.string(),
  photoUrl: z.string().nullable(),
  /** true, wenn Betrachter und Gastgeber bereits gegenseitig im Netzwerk sind. */
  connected: z.boolean(),
  /** true, wenn der Link der eigene ist — dann gibt es nichts zu vernetzen. */
  isSelf: z.boolean(),
});
export type JoinViewerDTO = z.infer<typeof JoinViewerDTO>;

export const JoinHostResponse = z.object({
  host: JoinHostDTO,
  viewer: JoinViewerDTO.nullable().default(null),
});
export type JoinHostResponse = z.infer<typeof JoinHostResponse>;

/** Ein Klick statt Formular: bestehendes Konto mit dem Gastgeber vernetzen. */
export const JoinConnectResponse = z.object({
  ok: z.literal(true),
  /** true, wenn die Verbindung schon vorher bestand — die UI meldet dann nichts Neues. */
  alreadyConnected: z.boolean(),
});
export type JoinConnectResponse = z.infer<typeof JoinConnectResponse>;

const optShort = z.string().trim().max(200).optional().nullable();
const optLong = z.string().trim().max(2000).optional().nullable();

export const JoinSubmitInput = z.object({
  firstName: z.string().trim().min(1).max(80),
  lastName: z.string().trim().min(1).max(80),
  email: z.string().trim().email().max(200),
  phone: z.string().trim().min(1).max(200),

  // Schritt 2 — optional
  linkedin: optShort,
  instagram: optShort,
  xing: optShort,
  website: optShort,

  // Schritt 3 — Bild
  photoUrl: z.string().trim().max(2_000_000).optional().nullable(),

  // Schritt 4+ — worum es eigentlich geht
  company: optShort,
  role: optShort,
  industry: optShort,
  city: optShort,
  country: optShort,
  offering: optLong,
  seeking: optLong,
  pitch: optLong,
  tags: z.array(z.string().trim().min(1).max(60)).max(20).optional(),

  // Einwilligungen. `acceptTerms` ist Pflicht — ohne Zustimmung keine Eintragung.
  acceptTerms: z.literal(true, {
    errorMap: () => ({ message: 'Den Nutzungsbedingungen muss zugestimmt werden.' }),
  }),
  newsletter: z.boolean().default(false),

  /**
   * Optionales eigenes Konto. Ist es gesetzt, wird zusätzlich ein Nutzer angelegt und sein
   * Profil aus genau diesen Antworten vorbefüllt — deshalb reicht am Ende ein Passwort.
   */
  createAccount: z
    .object({
      password: z.string().min(8).max(200),
    })
    .optional(),
});
export type JoinSubmitInput = z.infer<typeof JoinSubmitInput>;

export const JoinSubmitResponse = z.object({
  ok: z.literal(true),
  /** true, wenn zusätzlich ein Konto angelegt wurde — der Nutzer ist dann direkt angemeldet. */
  accountCreated: z.boolean(),
});
export type JoinSubmitResponse = z.infer<typeof JoinSubmitResponse>;

/** Der eigene Einladungslink (angemeldeter Bereich). */
export const InviteLinkResponse = z.object({
  slug: z.string(),
  /** Wie viele Personen sich über den Link eingetragen haben. */
  signups: z.number(),
});
export type InviteLinkResponse = z.infer<typeof InviteLinkResponse>;
